/**
 * useChatSession — text-only HTTP chat hook (Phase 5).
 *
 * Replaces the prior WebRTC / Azure Realtime data-channel transport for
 * the chat channel. Calls /api/chat-message which runs an OpenAI
 * Chat Completions tool loop server-side (same prompt + tools as voice)
 * and returns a single completed reply per turn. Voice still uses
 * Azure Realtime WebRTC — this file no longer touches RTC at all.
 *
 * Codex rules upheld:
 *  - #8 voice ↔ chat NEVER concurrent: caller still gates with `isMounted`.
 *  - #9 NO voice_recordings created (HTTP — no media tracks).
 *  - #11 customer opt-in still enforced server-side via enabled_channels.
 *
 * Public interface preserved (ChatModePanel imports unchanged):
 *  { status, messages, errorMessage, isToolCallInFlight,
 *    isAssistantThinking, sendMessage, reconnect }.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  postChatMessageStream,
  postChatTranscript,
  sendChatTranscriptBeacon,
} from '../api/voice-bot-client';
import type { ContactInfo, ScheduledMeeting, WidgetConfig } from '../types';
import type { CrossChannelMemory } from '../hooks/useCrossChannelMemory';
import { shouldFlush } from './flush-policy';

export type ChatStatus = 'idle' | 'connecting' | 'ready' | 'error' | 'closed';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  text: string;
  /** Kept for API compatibility with the prior streaming hook — always
   *  false now (the HTTP endpoint returns the full reply atomically). */
  inProgress?: boolean;
  /** When true, the message is sent to the model (as a user-role
   *  [SYSTEM: …] instruction) but NOT rendered as a chat bubble. Used
   *  by ChatModePanel to relay client-side events back to the bot
   *  (e.g. "Calendly booking confirmed"). */
  internal?: boolean;
}

export interface UseChatSessionOptions {
  /** Caller-controlled mount gate. When false the hook tears down any
   *  in-flight request and flushes the transcript. */
  isMounted: boolean;
  config: WidgetConfig;
  /**
   * Shared conversation memory that survives voice <-> chat mode switches.
   * When non-null AND `hasMemory()` returns true at mount, the hook seeds
   * `messages` from the snapshot and SKIPS the opening greeting so the bot
   * continues mid-conversation transparently. Every successful turn (user
   * + assistant reply) is also appended to this memory so a later switch
   * back to voice has the full history. Optional — when omitted, chat
   * behaves as a fresh session.
   */
  crossChannelMemory?: CrossChannelMemory;
}

export interface ChatToolEvent {
  name: string;
  args?: unknown;
  result?: unknown;
}

export interface UseChatSessionResult {
  status: ChatStatus;
  messages: ChatMessage[];
  errorMessage: string | null;
  /** True while a server-side tool loop is in flight. The HTTP endpoint
   *  hides individual tool calls from the client, so this tracks the
   *  whole request lifecycle. */
  isToolCallInFlight: boolean;
  /** True between user send and assistant reply — UI shows typing dots. */
  isAssistantThinking: boolean;
  sendMessage: (text: string) => void;
  /** Send a hidden "system note" as a user-role message. Used by the
   *  ChatModePanel to relay client-side events (e.g. Calendly booking
   *  confirmed, contact popup dismissed) back to the bot so it can
   *  respond appropriately. Does NOT add a visible bubble to the chat. */
  sendSystemNote: (text: string) => void;
  /** Reset the session: flush transcript, clear messages, re-fetch greeting. */
  reconnect: () => void;
  /** Mark the chat complete and flush its transcript before the panel closes. */
  completeSession: () => void;
  /** Flush the transcript to n8n NOW because a business-critical lead
   *  (booking / contact details) was just captured — WITHOUT closing the
   *  chat. The conversation may continue. Safe to call repeatedly: the
   *  server dedupes the n8n send (metadata.n8n.sentAt), so a later
   *  completeSession()/abandon flush never double-processes the lead. */
  flushLead: () => void;
  /** Customer ID resolved server-side from the public key. Needed by the
   *  client to call /api/calendly-book directly from the chat panel
   *  (book_meeting tool in chat is a two-step client/server dance — see
   *  chat-tool-dispatcher.dispatchBookMeeting). */
  customerId: string | null;
  /** Tool events from the most recent server turn. The ChatModePanel
   *  watches this to react to book_meeting (open contact form +
   *  Calendly iframe). Bumped fresh on every successful response so
   *  effects can re-fire on repeat calls to the same tool. */
  lastToolEvents: ChatToolEvent[];
  /** Monotonic counter bumped every time `lastToolEvents` is replaced.
   *  Lets consumers depend on identity (not just array length) when the
   *  model calls the same tool twice in a row with the same args. */
  toolEventsVersion: number;
  /** Record the contact details collected inline in the chat (name,
   *  email, phone). They are forwarded to n8n with the transcript flush
   *  as `contact.{name,email,phone}` — parity with the old details
   *  form payload. Pass null to clear. */
  reportContactInfo: (info: ContactInfo | null) => void;
  /** Record the scheduled meeting / callback the chat just produced so it
   *  is forwarded to n8n with the transcript flush as `meeting`. */
  reportScheduledMeeting: (meeting: ScheduledMeeting | null) => void;
}

const generateId = () =>
  `m_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

interface ChatMessagePayload {
  sessionId?: string | null;
  channel: 'chat';
  customerId?: string | null;
  reply?: string;
  toolEvents?: Array<{ name: string; args?: unknown; result?: unknown }>;
  usage?: { totalTokens?: number };
}

export function useChatSession(options: UseChatSessionOptions): UseChatSessionResult {
  const { isMounted, config, crossChannelMemory } = options;

  // Keep a ref to crossChannelMemory so callbacks (sendTurn) see the
  // latest reference without forcing them into the dep arrays.
  const crossChannelMemoryRef = useRef<CrossChannelMemory | undefined>(crossChannelMemory);
  useEffect(() => {
    crossChannelMemoryRef.current = crossChannelMemory;
  }, [crossChannelMemory]);

  const [status, setStatus] = useState<ChatStatus>('idle');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isToolCallInFlight, setIsToolCallInFlight] = useState(false);
  const [isAssistantThinking, setIsAssistantThinking] = useState(false);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [lastToolEvents, setLastToolEvents] = useState<ChatToolEvent[]>([]);
  const [toolEventsVersion, setToolEventsVersion] = useState<number>(0);
  /** Bumped by reconnect() to force the bootstrap effect to re-run. */
  const [reconnectKey, setReconnectKey] = useState(0);

  // Refs that survive closures and outlive React state writes.
  const messagesRef = useRef<ChatMessage[]>([]);
  const sessionIdRef = useRef<string | null>(null);
  const sessionStartMsRef = useRef<number>(0);
  const totalTokensRef = useRef<number>(0);
  /** Snapshot of the last transcript flush so we can re-flush to keep the
   *  stored transcript fresh (e.g. flush-on-lead, then a fuller flush at
   *  close) without spamming. The SERVER dedupes the n8n send by
   *  conversation_logs.metadata.n8n.sentAt, so re-POSTing only updates the
   *  stored row and never re-processes the lead. `null` = never flushed. */
  const lastFlushRef = useRef<{ count: number; outcome: 'completed' | 'abandoned' } | null>(null);
  /** Bumped on teardown so stale fetches drop their results on the floor. */
  const generationRef = useRef<number>(0);
  /** Synchronous double-send guard. React state is async, so a fast
   *  double-Enter could otherwise start two concurrent turns. */
  const inFlightRef = useRef<boolean>(false);
  /** Aborts the current /api/chat-message fetch on teardown/reconnect. */
  const abortRef = useRef<AbortController | null>(null);
  /** Structured lead captured inline in the chat, forwarded to n8n with
   *  the transcript flush (parity with the old details-form payload). */
  const contactInfoRef = useRef<ContactInfo | null>(null);
  const scheduledMeetingRef = useRef<ScheduledMeeting | null>(null);

  const reportContactInfo = useCallback((info: ContactInfo | null) => {
    contactInfoRef.current = info;
  }, []);
  const reportScheduledMeeting = useCallback((meeting: ScheduledMeeting | null) => {
    scheduledMeetingRef.current = meeting;
  }, []);

  /** Synchronous setter that keeps messagesRef in lockstep with state.
   *  Without this, pagehide/unmount immediately after a setMessages call
   *  could flush a stale transcript (Codex #14). */
  const updateMessages = useCallback(
    (updater: (prev: ChatMessage[]) => ChatMessage[]) => {
      setMessages((prev) => {
        const next = updater(prev);
        messagesRef.current = next;
        return next;
      });
    },
    [],
  );

  // Keep messagesRef in lockstep on any state change we didn't route
  // through updateMessages (defensive).
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // ----- Transcript persistence (mirrors voice path) -----
  const buildTranscriptPayload = useCallback(
    (outcome: 'completed' | 'abandoned') => {
      const sessionId = sessionIdRef.current;
      if (!sessionId) return null;
      const startedAt = sessionStartMsRef.current;
      const durationSeconds = startedAt > 0 ? Math.floor((Date.now() - startedAt) / 1000) : 0;
      const transcript = messagesRef.current
        .filter((m) => m.role === 'user' || m.role === 'assistant')
        .map((m) => ({ role: m.role, content: m.text }));
      return {
        sessionId,
        transcript,
        tokensUsed: totalTokensRef.current,
        durationSeconds,
        outcome,
        // Structured lead collected inline in the chat. The server
        // (api/chat-transcript.js) forwards these to n8n as
        // customerInfoPayload.contact / .meeting — so the lead arrives as
        // discrete fields, exactly like the old contact form used to send.
        contactInfo: contactInfoRef.current || null,
        scheduledMeeting: scheduledMeetingRef.current || null,
        pageContext:
          typeof window !== 'undefined'
            ? {
                url: window.location?.href,
                referrer: document.referrer || null,
                userAgent: navigator.userAgent,
              }
            : null,
      };
    },
    [],
  );

  const flushTranscript = useCallback(
    (outcome: 'completed' | 'abandoned', useBeacon: boolean) => {
      const payload = buildTranscriptPayload(outcome);
      if (!payload) return;
      // Skip totally-empty sessions (user opened panel then closed).
      if (payload.transcript.length === 0 && payload.tokensUsed === 0) return;

      // Re-flush guard (see flush-policy.shouldFlush). The server dedupes
      // the actual n8n send, so re-POSTing is cheap and never double-processes
      // a lead — this just avoids redundant identical flushes.
      const prev = lastFlushRef.current;
      if (!shouldFlush(prev, payload.transcript.length, outcome)) return;
      lastFlushRef.current = { count: payload.transcript.length, outcome };
      try {
        if (useBeacon && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
          sendChatTranscriptBeacon(payload, config.publicKey);
        } else {
          void postChatTranscript(payload, config.publicKey).catch((err) =>
            console.warn('[chat-session] transcript POST failed', err),
          );
        }
      } catch (err) {
        console.warn('[chat-session] transcript send threw', err);
      }
    },
    [buildTranscriptPayload, config.publicKey],
  );

  // ----- Core POST + state-update helper -----
  /**
   * Send a turn to /api/chat-message and append the assistant reply.
   * `userMessage` may be omitted on the first call to fetch the opening
   * greeting. Caller is responsible for any optimistic UI updates
   * (appending the user bubble) BEFORE invoking this.
   */
  const sendTurn = useCallback(
    async (userMessage: string | null) => {
      const generation = generationRef.current;
      // Cancel any prior in-flight fetch and start a fresh AbortController.
      if (abortRef.current) {
        try { abortRef.current.abort(); } catch { /* ignore */ }
      }
      const controller = new AbortController();
      abortRef.current = controller;
      setIsAssistantThinking(true);
      setIsToolCallInFlight(true);
      setErrorMessage(null);

      // Snapshot the history WITHOUT the new userMessage — server appends.
      const history = messagesRef.current
        .filter((m) => m.role === 'user' || m.role === 'assistant')
        .map((m) => ({ role: m.role, content: m.text }));
      // If we just optimistically appended the user message, drop it from
      // history (server will see it as `userMessage`).
      const historyForServer =
        userMessage &&
        history.length > 0 &&
        history[history.length - 1].role === 'user' &&
        history[history.length - 1].content === userMessage
          ? history.slice(0, -1)
          : history;

      try {
        const requestBody = {
          sessionId: sessionIdRef.current ?? undefined,
          messages: historyForServer,
          userMessage: userMessage ?? undefined,
          propertyCode: (config as unknown as { propertyCode?: string }).propertyCode,
        };

        // Shared finalizer for a fully-resolved turn (used by both the
        // SSE path's `done` event and the JSON fallback path).
        const applyResolvedTurn = (
          sessionId: string | null | undefined,
          custId: string | null | undefined,
          totalTokens: number | undefined,
          toolEvents: ChatToolEvent[],
          replyText: string,
        ) => {
          if (sessionId && !sessionIdRef.current) sessionIdRef.current = sessionId;
          if (typeof custId === 'string' && custId.length > 0) {
            setCustomerId((prev) => (prev === custId ? prev : custId));
          }
          if (typeof totalTokens === 'number') totalTokensRef.current += totalTokens;
          setLastToolEvents(toolEvents);
          setToolEventsVersion((v) => v + 1);
          const xMem = crossChannelMemoryRef.current;
          if (xMem) {
            if (userMessage && userMessage.trim()) {
              xMem.append({ role: 'user', content: userMessage, source: 'chat' });
            }
            if (replyText) xMem.append({ role: 'assistant', content: replyText, source: 'chat' });
          }
          if (sessionStartMsRef.current === 0) sessionStartMsRef.current = Date.now();
          setStatus('ready');
        };

        const resp = await postChatMessageStream(
          requestBody,
          config.publicKey,
          controller.signal,
        );

        if (generation !== generationRef.current) return; // teardown happened

        const contentType = resp.headers.get('content-type') || '';
        const isStream = resp.ok && contentType.includes('text/event-stream') && !!resp.body;

        if (!isStream) {
          // ── Non-streaming fallback (errors, or server returned JSON). ──
          if (!resp.ok) {
            let errText = `HTTP ${resp.status}`;
            try {
              const errJson = (await resp.json()) as { error?: string; message?: string };
              errText = errJson.message || errJson.error || errText;
            } catch {
              /* ignore JSON parse */
            }
            throw new Error(errText);
          }
          const data = (await resp.json()) as ChatMessagePayload;
          if (generation !== generationRef.current) return;
          const toolEvents: ChatToolEvent[] = Array.isArray(data.toolEvents) ? data.toolEvents : [];
          const replyText = (data.reply || '').trim();
          if (replyText) {
            updateMessages((prev) => [
              ...prev,
              { id: generateId(), role: 'assistant', text: replyText, inProgress: false },
            ]);
          }
          applyResolvedTurn(data.sessionId, data.customerId, data.usage?.totalTokens, toolEvents, replyText);
          return;
        }

        // ── SSE streaming path. ──
        let assistantId: string | null = null;
        let streamedText = '';
        let finalReply = '';
        let usageTotal: number | undefined;
        let metaSessionId: string | null = null;
        let metaCustomerId: string | null = null;
        let toolEvents: ChatToolEvent[] = [];

        const ensureBubble = () => {
          if (assistantId) return;
          assistantId = generateId();
          const id = assistantId;
          // First token arrived — swap the typing indicator for the
          // streaming bubble.
          setIsAssistantThinking(false);
          updateMessages((prev) => [
            ...prev,
            { id, role: 'assistant', text: '', inProgress: true },
          ]);
        };
        const setBubbleText = (txt: string) => {
          const id = assistantId;
          if (!id) return;
          updateMessages((prev) => prev.map((m) => (m.id === id ? { ...m, text: txt } : m)));
        };

        const reader = resp.body!.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        const processEvent = (rawEvent: string) => {
          let evName = 'message';
          let dataStr = '';
          for (const line of rawEvent.split('\n')) {
            if (line.startsWith('event:')) evName = line.slice(6).trim();
            else if (line.startsWith('data:')) dataStr += line.slice(5).replace(/^\s/, '');
          }
          if (!dataStr) return;
          let payload: Record<string, unknown>;
          try {
            payload = JSON.parse(dataStr);
          } catch {
            return;
          }

          if (evName === 'meta') {
            metaSessionId = (payload.sessionId as string) || null;
            metaCustomerId = (payload.customerId as string) || null;
            if (metaSessionId && !sessionIdRef.current) sessionIdRef.current = metaSessionId;
            if (metaCustomerId) {
              setCustomerId((prev) => (prev === metaCustomerId ? prev : metaCustomerId));
            }
          } else if (evName === 'token') {
            ensureBubble();
            streamedText += (payload.text as string) || '';
            setBubbleText(streamedText);
          } else if (evName === 'reset') {
            // The server discarded that draft (it preceded a tool call), so
            // the next tokens replace it instead of piling up after it.
            streamedText = '';
            setBubbleText('');
          } else if (evName === 'tool') {
            // Collect; the authoritative list arrives in `done`. We keep
            // a running copy so a stream that ends without `done`
            // (network cut) still surfaces booking events.
            toolEvents.push(payload as unknown as ChatToolEvent);
          } else if (evName === 'error') {
            throw new Error((payload.message as string) || 'chat stream error');
          } else if (evName === 'done') {
            finalReply = ((payload.reply as string) || '').trim();
            const usage = payload.usage as { totalTokens?: number } | undefined;
            if (typeof usage?.totalTokens === 'number') usageTotal = usage.totalTokens;
            if (Array.isArray(payload.toolEvents)) {
              toolEvents = payload.toolEvents as unknown as ChatToolEvent[];
            }
          }
        };

        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          if (generation !== generationRef.current) return;
          buffer += decoder.decode(value, { stream: true });
          let sep: number;
          // SSE events are separated by a blank line.
          while ((sep = buffer.indexOf('\n\n')) !== -1) {
            const rawEvent = buffer.slice(0, sep);
            buffer = buffer.slice(sep + 2);
            processEvent(rawEvent);
          }
        }
        // A last event that arrived without its trailing blank line would
        // otherwise be dropped — including `done`, the authoritative reply.
        buffer += decoder.decode();
        if (buffer.trim()) processEvent(buffer);

        if (generation !== generationRef.current) return;

        // Finalize the assistant bubble with the authoritative reply text
        // (covers server-side fallback / synthetic rewrites).
        const resolvedReply = finalReply || streamedText.trim();
        if (assistantId) {
          const id = assistantId;
          updateMessages((prev) =>
            prev.map((m) => (m.id === id ? { ...m, text: resolvedReply, inProgress: false } : m)),
          );
        } else if (resolvedReply) {
          updateMessages((prev) => [
            ...prev,
            { id: generateId(), role: 'assistant', text: resolvedReply, inProgress: false },
          ]);
        }

        applyResolvedTurn(
          metaSessionId,
          metaCustomerId,
          usageTotal,
          toolEvents,
          resolvedReply,
        );
      } catch (err) {
        if (generation !== generationRef.current) return;
        // Swallow aborts (caller-triggered teardown) silently.
        if ((err as { name?: string })?.name === 'AbortError') return;
        const msg = err instanceof Error ? err.message : String(err);
        console.warn('[chat-session] chat-message failed', msg);
        setErrorMessage(msg);
        setStatus('error');
      } finally {
        if (abortRef.current === controller) abortRef.current = null;
        if (generation === generationRef.current) {
          setIsAssistantThinking(false);
          setIsToolCallInFlight(false);
        }
        // Release the synchronous guard so the next send can proceed.
        inFlightRef.current = false;
      }
    },
    [config.publicKey, config, updateMessages],
  );

  // ----- Public sendMessage -----
  const sendMessage = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      // Synchronous guard — React state would be too late for a fast
      // double-Enter / double-click and could start two concurrent turns.
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      // Optimistic user bubble — use updateMessages so messagesRef is in
      // sync immediately (pagehide right after Enter must see this msg).
      updateMessages((prev) => [
        ...prev,
        { id: generateId(), role: 'user', text: trimmed, inProgress: false },
      ]);
      // Fire-and-forget — sendTurn manages its own state + releases inFlightRef.
      void sendTurn(trimmed);
    },
    [sendTurn, updateMessages],
  );

  // ----- Public sendSystemNote -----
  // Used by ChatModePanel to relay client-side events (Calendly booking
  // confirmed, contact popup dismissed) back to the bot WITHOUT
  // surfacing a visible user bubble in the transcript. The note is
  // sent to the model as a user-role "[SYSTEM: …]" instruction (same
  // pattern as voice's data-channel system injections). The message is
  // flagged `internal: true` so the panel filters it out of the bubble
  // list but it still flows through messagesRef → history-for-server.
  const sendSystemNote = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      const wrapped = `[SYSTEM: ${trimmed}]`;
      updateMessages((prev) => [
        ...prev,
        { id: generateId(), role: 'user', text: wrapped, inProgress: false, internal: true },
      ]);
      // Pass `wrapped` as userMessage so sendTurn's tail-strip dedup
      // matches and we don't double-send it.
      void sendTurn(wrapped);
    },
    [sendTurn, updateMessages],
  );

  // ----- Reconnect -----
  const reconnect = useCallback(() => {
    // Abort any in-flight fetch so it can't append a stale reply.
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch { /* ignore */ }
      abortRef.current = null;
    }
    inFlightRef.current = false;
    flushTranscript('abandoned', false);
    generationRef.current += 1;
    sessionIdRef.current = null;
    sessionStartMsRef.current = 0;
    totalTokensRef.current = 0;
    lastFlushRef.current = null;
    setMessages([]);
    messagesRef.current = [];
    setErrorMessage(null);
    setIsAssistantThinking(false);
    setIsToolCallInFlight(false);
    setCustomerId(null);
    setLastToolEvents([]);
    setToolEventsVersion(0);
    setStatus('connecting');
    setReconnectKey((k) => k + 1);
  }, [flushTranscript]);

  const completeSession = useCallback(() => {
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch { /* ignore */ }
      abortRef.current = null;
    }
    inFlightRef.current = false;
    flushTranscript('completed', false);
    generationRef.current += 1;
    setStatus('closed');
    setIsAssistantThinking(false);
    setIsToolCallInFlight(false);
  }, [flushTranscript]);

  // Flush-on-lead: a booking / contact lead was just captured. Push the
  // transcript to n8n right now so the business-critical lead is never
  // hostage to how the window eventually closes (idle, tab-close, or an
  // unreliable mobile beacon). Does NOT tear the session down — the chat
  // stays open and may continue; the server dedupes the n8n send.
  const flushLead = useCallback(() => {
    flushTranscript('completed', false);
  }, [flushTranscript]);

  // ----- Mount / unmount: fetch opening greeting -----
  useEffect(() => {
    if (!isMounted) {
      // Tear down: abort fetch, flush transcript as ABANDONED (the caller
      // unmounted us — if the user actually completed the conversation
      // it's safe to label abandoned; no data is lost either way).
      if (abortRef.current) {
        try { abortRef.current.abort(); } catch { /* ignore */ }
        abortRef.current = null;
      }
      inFlightRef.current = false;
      flushTranscript('abandoned', false);
      generationRef.current += 1;
      setStatus('closed');
      setIsAssistantThinking(false);
      setIsToolCallInFlight(false);
      return;
    }
    // Fresh session.
    setStatus('connecting');
    setErrorMessage(null);
    sessionIdRef.current = null;
    sessionStartMsRef.current = 0;
    totalTokensRef.current = 0;
    lastFlushRef.current = null;

    // Cross-channel handoff: if the shared memory already has prior
    // turns (user just switched from voice mid-conversation), seed
    // `messages` from the snapshot and SKIP the greeting. The bot will
    // continue from the last assistant turn as soon as the user types.
    // The first /api/chat-message POST will include this seeded history
    // so the server-side model sees the full prior context.
    const xMem = crossChannelMemoryRef.current;
    if (xMem && xMem.hasMemory()) {
      const prior = xMem.snapshot();
      const seeded: ChatMessage[] = prior.map((p) => ({
        id: generateId(),
        role: p.role,
        text: p.content,
        inProgress: false,
      }));
      updateMessages(() => seeded);
      // Mark session live immediately so UI exits "connecting" state.
      sessionStartMsRef.current = Date.now();
      setStatus('ready');
      inFlightRef.current = false;
    } else {
      inFlightRef.current = true;
      // Empty body → server returns the opening greeting.
      void sendTurn(null);
    }
    // Cleanup on unmount/dependency change. Default to 'abandoned' —
    // 'completed' is meaningless from a React cleanup callback (we cannot
    // know whether the user actually finished), and the prior unconditional
    // 'completed' mislabeled every dropped session.
    return () => {
      if (abortRef.current) {
        try { abortRef.current.abort(); } catch { /* ignore */ }
        abortRef.current = null;
      }
      inFlightRef.current = false;
      flushTranscript('abandoned', false);
      generationRef.current += 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMounted, reconnectKey, config.publicKey]);

  // ----- pagehide: best-effort beacon flush -----
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onPageHide = () => {
      flushTranscript('abandoned', true);
    };
    // visibilitychange→hidden is the most reliable "user is leaving" signal
    // on mobile Safari/Chrome, where pagehide/unload often don't fire when
    // the tab is backgrounded or the app is swiped away. Both use the
    // beacon path and the lastFlushRef snapshot guards against a double-send.
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        flushTranscript('abandoned', true);
      }
    };
    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [flushTranscript]);

  return {
    status,
    messages,
    errorMessage,
    isToolCallInFlight,
    isAssistantThinking,
    sendMessage,
    sendSystemNote,
    reconnect,
    completeSession,
    flushLead,
    customerId,
    lastToolEvents,
    toolEventsVersion,
    reportContactInfo,
    reportScheduledMeeting,
  };
}
